/* eslint-disable no-console */
import { useMemo, useCallback } from "react";
import { combinarPorCorte, esMesHistorico } from "../../../utils/carteleria/corteHistorico";
import { calcIngresosHardwareHistorico } from "../../../utils/carteleria/historico/ingresosHardwareERHistorico";
import { calcIngresosHardwareMotor } from "../../../utils/carteleria/ingresosHardwareERMotor";

export function useHardwareCalcs({
  pais,
  selectedYear,
  monthsToShow,
  negociosCarteleria,
  negociosCarteleriaChile,
  negociosCarteleriaPeru,
  costosHardwareFlex,
  costosHardwareFlexAnoAnterior,
  costosHardwareFlexChile,
  costosHardwareFlexPeru,
  costosGestionData,
  getUFValue,
  getDolarValue,
  getDolarValueChile,
  getDolarValuePeru,
}) {
  // ---------------------------------------------------------------------------
  // Helper: calcCostosPorTipo
  // The same pattern is repeated 16 times (4 types x 4 datasets). Single helper.
  // ---------------------------------------------------------------------------
  const calcCostosPorTipo = useCallback((costosArray) => {
    const tipos = ["compra_hardware", "instalacion", "materiales", "otros_costos"];
    const result = {};
    tipos.forEach((tipo) => {
      const mapa = Array(12).fill(0);
      costosArray.forEach((c) => {
        if (c.tipo === tipo) {
          const i = c.mes - 1;
          if (i >= 0 && i < 12) mapa[i] += Number(c.monto) || 0;
        }
      });
      result[tipo] = mapa;
    });
    return result;
  }, []);

  // ---------------------------------------------------------------------------
  // Helper: calcIngresosHardware
  // Parametrized revenue calculation used by all 4 variants.
  // F3d (rediseño de Cartelería, "lo histórico no se toca"): los meses ANTERIORES a 2026-10 salen de la lógica
  // histórica congelada (`utils/carteleria/historico/ingresosHardwareERHistorico.js`, copia literal del código
  // anterior); los meses desde 2026-10 salen del motor `calendarioNegocio` (`ingresosHardwareERMotor.js`).
  // - paisCalc: "Chile" uses UF conversion, "Peru" does not
  // - getDolarFn: the specific dollar function to use
  // - costosGestion: only used for OPEX invoice lookup (null for prior year / Peru)
  // - Default moneda: Chile="UF", Peru="PEN"
  // - getUFValue comes from closure (always same function regardless of variant)
  // ---------------------------------------------------------------------------
  const calcIngresosHardware = useCallback(
    (negocios, year, paisCalc, getDolarFn, costosGestion) => {
      const hayHistorico = esMesHistorico(year, 0);
      const hayMotor = !esMesHistorico(year, 11);
      const historico = hayHistorico
        ? calcIngresosHardwareHistorico(negocios, year, paisCalc, getDolarFn, costosGestion, getUFValue)
        : null;
      if (!hayMotor) return historico;
      const motor = calcIngresosHardwareMotor(
        negocios,
        year,
        paisCalc,
        getDolarFn,
        costosGestion,
        getUFValue,
        new Date()
      );
      return historico ? combinarPorCorte(year, historico, motor) : motor;
    },
    [getUFValue]
  );

  // ---------------------------------------------------------------------------
  // Revenue: 4 variants using the parametrized helper
  // ---------------------------------------------------------------------------
  const ingresosHardwareMensual = useMemo(
    () => calcIngresosHardware(negociosCarteleria, selectedYear, pais, getDolarValue, costosGestionData),
    [calcIngresosHardware, negociosCarteleria, selectedYear, pais, getDolarValue, costosGestionData]
  );

  const añoAnteriorHW = selectedYear - 1;
  const ingresosHardwareMensualAnoAnterior = useMemo(
    () => calcIngresosHardware(negociosCarteleria, añoAnteriorHW, pais, getDolarValue, null),
    [calcIngresosHardware, negociosCarteleria, añoAnteriorHW, pais, getDolarValue]
  );

  const ingresosHardwareMensualChile = useMemo(
    () => calcIngresosHardware(negociosCarteleriaChile, selectedYear, "Chile", getDolarValueChile, costosGestionData),
    [calcIngresosHardware, negociosCarteleriaChile, selectedYear, getDolarValueChile, costosGestionData]
  );

  const ingresosHardwareMensualPeru = useMemo(
    () => calcIngresosHardware(negociosCarteleriaPeru, selectedYear, "Perú", getDolarValuePeru, null),
    [calcIngresosHardware, negociosCarteleriaPeru, selectedYear, getDolarValuePeru]
  );

  // ---------------------------------------------------------------------------
  // Costs: 4 datasets x 4 types = 16, using calcCostosPorTipo
  // ---------------------------------------------------------------------------
  const costosCurrent = useMemo(() => calcCostosPorTipo(costosHardwareFlex), [calcCostosPorTipo, costosHardwareFlex]);
  const costosAnoAnterior = useMemo(
    () => calcCostosPorTipo(costosHardwareFlexAnoAnterior),
    [calcCostosPorTipo, costosHardwareFlexAnoAnterior]
  );
  const costosChile = useMemo(
    () => calcCostosPorTipo(costosHardwareFlexChile),
    [calcCostosPorTipo, costosHardwareFlexChile]
  );
  const costosPeru = useMemo(
    () => calcCostosPorTipo(costosHardwareFlexPeru),
    [calcCostosPorTipo, costosHardwareFlexPeru]
  );

  // Destructure into individual arrays for backwards-compatible return values
  const compraHardwareMensual = costosCurrent.compra_hardware;
  const instalacionMensual = costosCurrent.instalacion;
  const materialesMensual = costosCurrent.materiales;
  const otrosCostosMensual = costosCurrent.otros_costos;

  const compraHardwareMensualAnoAnterior = costosAnoAnterior.compra_hardware;
  const instalacionMensualAnoAnterior = costosAnoAnterior.instalacion;
  const materialesMensualAnoAnterior = costosAnoAnterior.materiales;
  const otrosCostosMensualAnoAnterior = costosAnoAnterior.otros_costos;

  const compraHardwareMensualChile = costosChile.compra_hardware;
  const instalacionMensualChile = costosChile.instalacion;
  const materialesMensualChile = costosChile.materiales;
  const otrosCostosMensualChile = costosChile.otros_costos;

  const compraHardwareMensualPeru = costosPeru.compra_hardware;
  const instalacionMensualPeru = costosPeru.instalacion;
  const materialesMensualPeru = costosPeru.materiales;
  const otrosCostosMensualPeru = costosPeru.otros_costos;

  // ---------------------------------------------------------------------------
  // YTDs del bloque B
  // ---------------------------------------------------------------------------
  const ytdIngresosHardware = useMemo(
    () => ingresosHardwareMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [ingresosHardwareMensual, monthsToShow]
  );

  const ytdCompraHardware = useMemo(
    () => compraHardwareMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [compraHardwareMensual, monthsToShow]
  );

  const ytdInstalacion = useMemo(
    () => instalacionMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [instalacionMensual, monthsToShow]
  );

  const ytdMateriales = useMemo(
    () => materialesMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [materialesMensual, monthsToShow]
  );

  const ytdOtrosCostosHardware = useMemo(
    () => otrosCostosMensual.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
    [otrosCostosMensual, monthsToShow]
  );

  const ytdMargenHardware = useMemo(
    () => ytdIngresosHardware - (ytdCompraHardware + ytdInstalacion + ytdMateriales + ytdOtrosCostosHardware),
    [ytdIngresosHardware, ytdCompraHardware, ytdInstalacion, ytdMateriales, ytdOtrosCostosHardware]
  );

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------
  return {
    // Revenue
    ingresosHardwareMensual,
    ingresosHardwareMensualAnoAnterior,
    ingresosHardwareMensualChile,
    ingresosHardwareMensualPeru,
    // Costs current
    compraHardwareMensual,
    instalacionMensual,
    materialesMensual,
    otrosCostosMensual,
    // Costs prior year
    compraHardwareMensualAnoAnterior,
    instalacionMensualAnoAnterior,
    materialesMensualAnoAnterior,
    otrosCostosMensualAnoAnterior,
    // Costs Chile
    compraHardwareMensualChile,
    instalacionMensualChile,
    materialesMensualChile,
    otrosCostosMensualChile,
    // Costs Peru
    compraHardwareMensualPeru,
    instalacionMensualPeru,
    materialesMensualPeru,
    otrosCostosMensualPeru,
    // YTDs
    ytdIngresosHardware,
    ytdCompraHardware,
    ytdInstalacion,
    ytdMateriales,
    ytdOtrosCostosHardware,
    ytdMargenHardware,
  };
}
